import { useRef, useCallback, useEffect, useMemo } from 'react';

/** Matches desktop mega-menu root exit when configured on `NavigationMenu.Root`. */
export const DROPDOWN_CLOSE_DELAY_MS = 500;

interface UseDropdownDelayOptions {
    delay?: number;
    onHide?: () => void;
}

interface UseDropdownDelayReturn {
    handleMouseEnter: () => void;
    handleMouseLeave: () => void;
    cancelDelay: () => void;
    hideImmediately: () => void;
}

const useDropdownDelay = ({
    delay = DROPDOWN_CLOSE_DELAY_MS,
    onHide,
}: UseDropdownDelayOptions): UseDropdownDelayReturn => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelDelay = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const handleMouseEnter = useCallback(() => {
        cancelDelay();
    }, [cancelDelay]);

    const handleMouseLeave = useCallback(() => {
        cancelDelay();
        timeoutRef.current = setTimeout(() => {
            onHide?.();
            timeoutRef.current = null;
        }, delay);
    }, [delay, onHide, cancelDelay]);

    const hideImmediately = useCallback(() => {
        cancelDelay();
        onHide?.();
    }, [onHide, cancelDelay]);

    useEffect(() => () => cancelDelay(), [cancelDelay]);

    return useMemo(() => ({
        handleMouseEnter,
        handleMouseLeave,
        cancelDelay,
        hideImmediately,
    }), [handleMouseEnter, handleMouseLeave, cancelDelay, hideImmediately]);
};

export default useDropdownDelay;
